/* One skill: who made it and where it lives, its SKILL.md, every file, every audit — or,
   for the workspace's own, the agents that have it, and Edit and Delete for whoever
   may change it (its creator, or an admin). */
import React, { useState } from "react";
import { BookOpen, Bot, Code, FileCode2, GitBranch, Globe, Pencil, ShieldCheck, Trash2 } from "lucide-react";

import { useSettings } from "@/shared/api/settings";
import { isSkillActionLive, useSkill, useSkillFile } from "@/shared/api/skills";
import { AuditPill, SourceBadge } from "@/shared/components/skill-atoms";
import CodeBlock from "@/shared/components/markdown/CodeBlock";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { GhostButton, IconButton, PageHeader } from "@/shared/components/ui-kit";

import { AUDITORS, WORKS_WITH, formatCount, installCommand } from "../catalog";
import { CopyableCommand, LoadingDots, OwnerAvatar } from "./atoms";
import DeleteSkillDialog from "./DeleteSkillDialog";
import FileBrowser from "./FileBrowser";
import SkillMarkdown from "./SkillMarkdown";
import { UsedBy, WorkspaceCredits } from "./WorkspaceSkill";

import type { LucideIcon } from "lucide-react";
import type { ApiError } from "@/shared/api/errors";
import type { SkillAudit, SkillDetail as Detail } from "@/shared/types";

function MetaChip({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-[7px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] text-[11.5px] max-w-full">
      <span className="text-[var(--text-quaternary)] shrink-0">{label}</span>
      <code className="text-[var(--text-secondary)] truncate">{value}</code>
    </span>
  );
}

function Overview({ skill }: { skill: Detail }) {
  const [raw, setRaw] = useState(false);
  const hasSkillMd = skill.files.some((f) => f.path === "SKILL.md");
  const md = useSkillFile(skill.id, hasSkillMd ? "SKILL.md" : null);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        <MetaChip label="version" value={skill.version} />
        <MetaChip label="license" value={skill.license ?? (skill.source === "workspace" ? null : "none")} />
        <MetaChip label="allowed-tools" value={skill.allowed_tools.join(" ") || null} />
        <MetaChip label="compatibility" value={skill.compatibility} />
        {hasSkillMd && (
          <button
            onClick={() => setRaw((v) => !v)}
            aria-pressed={raw}
            className={`press ml-auto flex items-center gap-1.5 h-7 px-2.5 rounded-[8px] text-[12px] ring-1 ring-[var(--hairline)] ${
              raw
                ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
            }`}
          >
            <Code className="w-3 h-3" strokeWidth={1.75} />
            {raw ? "Rendered" : "View raw"}
          </button>
        )}
      </div>
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-5 py-4 min-w-0">
        {!hasSkillMd && <p className="text-[13px] text-[var(--text-secondary)] m-0">{skill.description}</p>}
        {md.isPending && hasSkillMd && <LoadingDots label="Loading SKILL.md" />}
        {md.isError && (
          <p className="text-[13px] text-[var(--text-secondary)] m-0">
            {(md.error as ApiError).status === 404
              ? "SKILL.md isn't stored — the skill needs importing again."
              : "Couldn't load SKILL.md."}
          </p>
        )}
        {md.data?.text != null && (raw ? <CodeBlock code={md.data.text} lang="markdown" title="SKILL.md" /> : <SkillMarkdown text={md.data.text} />)}
      </div>
    </div>
  );
}

function AuditRow({ label, audit }: { label: string; audit: SkillAudit | undefined }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-t hairline first:border-t-0">
      <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-[2px] text-[var(--text-tertiary)]" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium tracking-[-0.005em]">{label}</div>
        <div className="text-[12px] text-[var(--text-secondary)] mt-[1px] leading-[1.55]">
          {audit ? audit.summary || "No summary given." : "Hasn't scanned this skill yet."}
        </div>
        {audit && (
          <div className="flex items-center gap-2 flex-wrap mt-1 text-[11.5px] text-[var(--text-quaternary)]">
            {audit.risk && <span className="font-mono">risk {audit.risk}</span>}
            {audit.audited_at && <span className="font-mono tabular-nums">{new Date(audit.audited_at).toLocaleDateString()}</span>}
            {audit.categories.map((c) => (
              <span key={c} className="font-mono">{c.toLowerCase().replace(/_/g, " ")}</span>
            ))}
          </div>
        )}
      </div>
      <AuditPill status={audit?.status ?? "none"} />
    </div>
  );
}

function Security({ skill }: { skill: Detail }) {
  const known = new Set(AUDITORS.map((a) => a.key));
  const others = Object.entries(skill.audits).filter(([key]) => !known.has(key));
  return (
    <div className="space-y-3">
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
        {AUDITORS.map((a) => <AuditRow key={a.key} label={a.label} audit={skill.audits[a.key]} />)}
        {others.map(([key, audit]) => <AuditRow key={key} label={audit.provider || key} audit={audit} />)}
      </div>
      <p className="text-[12px] text-[var(--text-tertiary)] leading-[1.6] m-0">
        These are skills.sh's security partners, read when the skill was imported. A Warn or Fail is a finding to
        read before you rely on the skill, not a block.
      </p>
    </div>
  );
}

function Credits({ skill, onOpenRepo }: { skill: Detail; onOpenRepo: (repo: string) => void }) {
  const link = "inline-flex items-center gap-1 min-w-0 hover:text-[var(--text-primary)] hover:underline";
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-4 py-3 flex items-center gap-3 flex-wrap">
      <OwnerAvatar owner={skill.owner} size={32} rounded={9} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium tracking-[-0.005em] truncate">
          {skill.owner.url ? (
            <a href={skill.owner.url} target="_blank" rel="noreferrer" className="hover:underline">{skill.owner.login}</a>
          ) : skill.owner.login}
          {skill.author && skill.author !== skill.owner.login && (
            <span className="font-normal text-[var(--text-tertiary)]"> · written by {skill.author}</span>
          )}
        </div>
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-[2px] text-[12px] text-[var(--text-tertiary)]">
          {skill.repo && (
            <button onClick={() => onOpenRepo(skill.repo as string)} className={`press ${link}`}>
              <GitBranch className="w-3 h-3 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{skill.repo}</span>
            </button>
          )}
          {skill.folder_url && (
            <a href={skill.folder_url} target="_blank" rel="noreferrer" className={link}>
              <Globe className="w-3 h-3 shrink-0" strokeWidth={1.75} />
              GitHub
            </a>
          )}
          {skill.skills_sh_url && (
            <a href={skill.skills_sh_url} target="_blank" rel="noreferrer" className={link}>
              <Globe className="w-3 h-3 shrink-0" strokeWidth={1.75} />
              skills.sh
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

type Tab = { id: string; label: string; icon: LucideIcon };
type Meta = { label: string; value: string | null };

/** The page's top: the skill, what it does, and whatever a kind of skill adds under that. */
function Hero({ skill, children }: { skill: Detail; children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <OwnerAvatar owner={skill.owner} size={48} rounded={12} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <h1 className="text-[17px] font-semibold tracking-[-0.01em] m-0 break-words min-w-0">{skill.name}</h1>
          <SourceBadge source={skill.source} size={15} />
        </div>
        <p className="text-[13px] leading-[1.6] text-[var(--text-secondary)] mt-2 mb-0">{skill.description}</p>
        {children}
      </div>
    </div>
  );
}

function MetaStrip({ items }: { items: (Meta | false | null)[] }) {
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-4 py-3 flex items-center gap-6 overflow-x-auto no-scrollbar">
      {items.filter((m): m is Meta => !!m).map((m) => (
        <div key={m.label} className="shrink-0">
          <div className="text-[14px] font-semibold font-mono tabular-nums tracking-[-0.01em] whitespace-nowrap">{m.value}</div>
          <div className="text-[11px] text-[var(--text-tertiary)] mt-[1px] whitespace-nowrap">{m.label}</div>
        </div>
      ))}
    </div>
  );
}

function Tabs({ tabs, tab, onTab }: { tabs: Tab[]; tab: string; onTab: (id: string) => void }) {
  return (
    <div className="overflow-x-auto no-scrollbar">
      <div className="flex items-center rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-[3px] w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => onTab(t.id)}
            className={`press flex items-center gap-1.5 px-3 py-[4px] rounded-[7px] text-[12.5px] font-medium tabular-nums whitespace-nowrap ${
              tab === t.id
                ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ring-1 ring-[var(--hairline)]"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            }`}
          >
            <t.icon className="w-3 h-3" strokeWidth={1.75} />
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const BODY = "max-w-[880px] mx-auto px-4 md:px-6 py-6 space-y-4 fade-in-up";

/** A skill the catalog imported: its GitHub owner, stars, install command and audits. */
function CatalogBody({ skill, onOpenRepo }: { skill: Detail; onOpenRepo: (repo: string) => void }) {
  const [tab, setTab] = useState("overview");
  const audited = Object.keys(skill.audits).length;
  return (
    <div className={BODY}>
      <Hero skill={skill}>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <span className="text-[11px] text-[var(--text-quaternary)] pr-0.5">Works with</span>
          {WORKS_WITH.map((w) => (
            <span key={w} className="px-1.5 py-[2px] rounded-[5px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] text-[10.5px] text-[var(--text-tertiary)] whitespace-nowrap">
              {w}
            </span>
          ))}
        </div>
      </Hero>
      <Credits skill={skill} onOpenRepo={onOpenRepo} />
      {skill.repo && <CopyableCommand cmd={installCommand(skill.repo, skill.slug)} />}
      <MetaStrip items={[
        skill.stars != null && { label: "GitHub stars", value: formatCount(skill.stars) },
        { label: "Files", value: String(skill.files.length) },
        { label: "Audits", value: `${audited} of ${Math.max(audited, AUDITORS.length)}` },
        { label: "Version", value: skill.version },
        { label: "License", value: skill.license ?? "none" },
        { label: "Imported", value: new Date(skill.updated_at).toLocaleDateString() },
      ]}
      />
      <Tabs
        tab={tab}
        onTab={setTab}
        tabs={[
          { id: "overview", label: "Overview", icon: BookOpen },
          { id: "files", label: `Files · ${skill.files.length}`, icon: FileCode2 },
          { id: "security", label: "Security", icon: ShieldCheck },
        ]}
      />
      {tab === "overview" && <Overview skill={skill} />}
      {tab === "files" && <FileBrowser skill={skill} />}
      {tab === "security" && <Security skill={skill} />}
    </div>
  );
}

/** The workspace's own: who made it, and the agents that use it, in place of audits. */
function WorkspaceBody({ skill, onOpenAgent }: { skill: Detail; onOpenAgent?: (agentId: string) => void }) {
  const [tab, setTab] = useState("overview");
  const n = skill.used_by.length;
  return (
    <div className={BODY}>
      <Hero skill={skill} />
      <WorkspaceCredits skill={skill} />
      <MetaStrip items={[
        { label: "Files", value: String(skill.files.length) },
        { label: "Used by", value: `${n} agent${n === 1 ? "" : "s"}` },
        { label: "Version", value: skill.version },
        skill.license ? { label: "License", value: skill.license } : null,
        { label: "Changed", value: new Date(skill.updated_at).toLocaleDateString() },
      ]}
      />
      <Tabs
        tab={tab}
        onTab={setTab}
        tabs={[
          { id: "overview", label: "Overview", icon: BookOpen },
          { id: "files", label: `Files · ${skill.files.length}`, icon: FileCode2 },
          { id: "agents", label: `Used by · ${n}`, icon: Bot },
        ]}
      />
      {tab === "overview" && <Overview skill={skill} />}
      {tab === "files" && <FileBrowser skill={skill} />}
      {tab === "agents" && <UsedBy skill={skill} onOpenAgent={onOpenAgent} />}
    </div>
  );
}

/** Edit and Delete for whoever may change the skill; off, saying so, until their backend lands. */
function ChangeButtons({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const editLive = isSkillActionLive("edit");
  const deleteLive = isSkillActionLive("delete");
  return (
    <>
      <span title={editLive ? undefined : "Editing skills isn't available yet"}>
        <GhostButton onClick={onEdit} disabled={!editLive}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Edit
        </GhostButton>
      </span>
      <span title={deleteLive ? undefined : "Deleting skills isn't available yet"}>
        <IconButton onClick={onDelete} disabled={!deleteLive} title="Delete skill" aria-label="Delete skill" className="hover:text-[var(--danger)] disabled:opacity-40 disabled:pointer-events-none">
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        </IconButton>
      </span>
    </>
  );
}

export default function SkillDetail({ id, onBack, onOpenRepo, onEdit, onDeleted, onOpenAgent }: {
  id: string;
  onBack: () => void;
  onOpenRepo: (repo: string) => void;
  onEdit: () => void;
  onDeleted: () => void;
  onOpenAgent?: (agentId: string) => void;
}) {
  const q = useSkill(id);
  const { data: settings } = useSettings();
  const [deleting, setDeleting] = useState(false);
  const role = settings?.org?.role;
  const mayChange = q.data?.source === "workspace"
    && (role === "owner" || role === "admin" || q.data.created_by?.id === settings?.user.id);
  return (
    <>
      <PageHeader
        title={
          <Breadcrumb parent={{ label: "Skills", onClick: onBack }}>
            {q.data && (
              <>
                <OwnerAvatar owner={q.data.owner} size={20} rounded={5} />
                <span className="truncate">{q.data.name}</span>
                <SourceBadge source={q.data.source} />
              </>
            )}
          </Breadcrumb>
        }
      >
        {mayChange && <ChangeButtons onEdit={onEdit} onDelete={() => setDeleting(true)} />}
      </PageHeader>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {q.isPending && <LoadingDots label="Loading skill" />}
        {q.isError && (
          <div className="py-16 text-center text-[13px] text-[var(--text-secondary)]">
            {(q.error as ApiError).status === 404 ? "This skill isn't in the catalog." : "Couldn't load this skill."}
          </div>
        )}
        {q.data && (q.data.source === "workspace"
          ? <WorkspaceBody skill={q.data} onOpenAgent={onOpenAgent} />
          : <CatalogBody skill={q.data} onOpenRepo={onOpenRepo} />)}
      </div>
      {deleting && q.data && (
        <DeleteSkillDialog skill={q.data} onDeleted={onDeleted} onOpenAgent={onOpenAgent} onClose={() => setDeleting(false)} />
      )}
    </>
  );
}
